package er.prateek.crossly.progression

import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.progression.achievements.AchievementManager
import er.prateek.crossly.progression.achievements.PlayerStats

/**
 * The single place every finished game reports to. Each play screen records its own stars (as
 * before); it then calls [onGameCompleted], which advances the any-game streak and re-checks
 * badges. Keeping this out of the screens means a new mode gets streaks and badges by making
 * one call, and the rules can't drift between modes.
 */
class PlayerJourney(
    val streak: StreakManager,
    val achievements: AchievementManager,
    private val stats: () -> PlayerStats,
    private val analytics: AnalyticsLogger,
    /** Fired after anything that changes what tonight's reminder should say. */
    private val onProgressChanged: () -> Unit = {},
) {
    fun onGameCompleted(mode: GameMode, isDaily: Boolean = false): StreakUpdate {
        val update = streak.recordPlay()
        if (update.extended) {
            analytics.logEvent(
                AnalyticsEvents.PLAY_STREAK_EXTENDED,
                mapOf(
                    "mode" to mode.analyticsName,
                    "is_daily" to isDaily,
                    "current_streak" to update.after,
                    "longest_streak" to streak.streak.longest,
                ),
            )
        }
        update.lostStreak?.let { lost ->
            analytics.logEvent(AnalyticsEvents.PLAY_STREAK_LOST, mapOf("previous_streak" to lost))
        }
        achievements.evaluate(stats())
        onProgressChanged()
        return update
    }

    /** Ends a full Alphabets -> Maths -> Riddles Brainstorm round. */
    fun onBrainstormRoundCompleted() {
        val rounds = achievements.recordBrainstormRound()
        analytics.logEvent(AnalyticsEvents.BRAINSTORM_ROUND_COMPLETED, mapOf("rounds_total" to rounds))
        achievements.evaluate(stats())
    }

    fun onStreakRestored(method: String, restoredStreak: Int) {
        analytics.logEvent(
            AnalyticsEvents.STREAK_RESTORED,
            mapOf("method" to method, "restored_streak" to restoredStreak),
        )
        achievements.evaluate(stats())
        onProgressChanged()
    }

    /** App start: back-fill badges for existing players silently, and catch up anything missed. */
    fun onAppStarted() {
        achievements.seedIfNeeded(stats())
        achievements.evaluate(stats())
    }

    fun currentStats(): PlayerStats = stats()
}
